import {
  DRAFT_SITUATIONS,
  DRAFT_DIFFERENTIATORS,
} from "./content/situations/draft-situations.js";
import { SITUATIONS_NINE } from "./content/situations/situations-nine.js";
import { SITUATIONS_CONTRADICTIONS } from "./content/situations/situations-contradictions.js";
import { DIFFERENTIATORS_NINE } from "./content/situations/differentiators-nine.js";
import type { PatternId } from "./content/types.js";
import { PATTERN_2 } from "./content/patterns/pattern-2.js";
import { PATTERN_3 } from "./content/patterns/pattern-3.js";
import { PATTERN_4 } from "./content/patterns/pattern-4.js";
import { PATTERN_5 } from "./content/patterns/pattern-5.js";
import { PATTERN_7 } from "./content/patterns/pattern-7.js";
import { PATTERN_8 } from "./content/patterns/pattern-8.js";
import { PATTERN_1 } from "./content/patterns/pattern-1.js";
import { PATTERN_6 } from "./content/patterns/pattern-6.js";
import { PATTERN_9 } from "./content/patterns/pattern-9.js";
import { EXPERIMENTS } from "./content/experiments/experiments.js";
import { classify, differentiatorFor, needsDifferentiation } from "./engine/classify.js";
import { recommend } from "./engine/recommend.js";
import type { ClassificationResult } from "./engine/engine.types.js";
import {
  answerQuestion,
  createSession,
  giveConsent,
  goBack,
  goToScreen,
  scheduleDifferentiation,
  setReaction,
  type SessionState,
} from "./state/session.js";
import { loadSession, saveSession, clearSession } from "./state/storage.js";
import { renderIntro } from "./ui/screens/intro.js";
import { renderConsent } from "./ui/screens/consent.js";
import { renderQuestion } from "./ui/screens/question.js";
import { renderResult } from "./ui/screens/result.js";
import { renderReaction } from "./ui/screens/reaction.js";
import { renderRecommendation } from "./ui/screens/recommendation.js";
import { renderSave } from "./ui/screens/save.js";
import { renderTracePanel } from "./debug/trace-panel.js";
import { track } from "./analytics/events.js";

// CONTENIDO REAL, de punta a punta. Las situaciones dejaron de ser de relleno
// el 17 de agosto de 2026, cuando el autor puso los 26 pesos: escribirlas era
// autoría asistida, pero el scoring lo reserva el DEFINITION_OF_DONE a una
// persona, y hasta ese momento el banco activo tenía que seguir siendo el
// falso. `draft-situations.test.ts` sostiene la regla en las dos direcciones.
const bank = {
  situations: [...DRAFT_SITUATIONS, ...SITUATIONS_NINE, ...SITUATIONS_CONTRADICTIONS],
  differentiationQuestions: [...DRAFT_DIFFERENTIATORS, ...DIFFERENTIATORS_NINE],
};
const patterns = {
  "1": PATTERN_1, "2": PATTERN_2, "3": PATTERN_3, "4": PATTERN_4, "5": PATTERN_5,
  "6": PATTERN_6, "7": PATTERN_7, "8": PATTERN_8, "9": PATTERN_9,
};
const patternIds: PatternId[] = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];
const situationOrder = bank.situations.filter((s) => s.required).map((s) => s.id);

const app = document.getElementById("app");
if (!app) throw new Error("main: #app not found");

let state: SessionState = loadSession<SessionState>() ?? createSession(situationOrder);
if (!state.answers) state = createSession(situationOrder); // guard against a corrupt/older stored shape

const ctx = () => ({ contentVersion: "fake-0.1", engineVersion: "0.1.0-alpha" });

function persistAndRender(next: SessionState): void {
  state = next;
  saveSession(state);
  render();
}

function currentResult(): ClassificationResult {
  return classify(state.answers, bank, patternIds);
}

function advanceAfterAnswer(): void {
  const nextSituation = situationOrder[state.currentIndex];
  if (nextSituation) {
    persistAndRender(state);
    return;
  }
  const desempate = needsDifferentiation(state.answers, bank, patternIds, state.differentiationAsked)
    ? differentiatorFor(state.answers, bank, patternIds)
    : null;
  if (desempate) {
    persistAndRender(scheduleDifferentiation(state, desempate.id));
    return;
  }
  const result = currentResult();
  track({ name: "result_generated", sessionId: state.sessionId, resultState: result.state }, ctx());
  persistAndRender(goToScreen(state, "result"));
}

function render(): void {
  app!.replaceChildren();

  switch (state.screen) {
    case "intro": {
      renderIntro(app!, () => {
        track({ name: "session_started", sessionId: state.sessionId }, ctx());
        persistAndRender(goToScreen(state, "consent"));
      });
      return;
    }
    case "consent": {
      renderConsent(app!, () => {
        track({ name: "intro_completed", sessionId: state.sessionId }, ctx());
        persistAndRender(goToScreen(giveConsent(state), "situations"));
      });
      return;
    }
    case "situations": {
      const situationId = situationOrder[state.currentIndex];
      const situation = bank.situations.find((s) => s.id === situationId);
      if (!situation) {
        advanceAfterAnswer();
        return;
      }
      track({ name: "question_viewed", sessionId: state.sessionId, questionId: situation.id }, ctx());
      renderQuestion(app!, {
        eyebrow: "Situation",
        prompt: situation.prompt,
        progressLabel: `Question ${state.currentIndex + 1} of ${situationOrder.length}`,
        options: situation.options,
        selectedOptionId: state.answers.find((a) => a.questionId === situation.id)?.optionId,
        onSelect: (optionId) => {
          const isChange = state.answers.some((a) => a.questionId === situation.id);
          track(
            {
              name: isChange ? "answer_changed" : "answer_submitted",
              sessionId: state.sessionId,
              questionId: situation.id,
            },
            ctx(),
          );
          const next = answerQuestion(state, { questionId: situation.id, optionId });
          state = next;
          advanceAfterAnswer();
        },
        onBack: state.currentIndex > 0 ? () => persistAndRender(goBack(state)) : undefined,
      });
      return;
    }
    case "differentiation": {
      const questionId = state.differentiationOrder[state.differentiationOrder.length - 1];
      const question = bank.differentiationQuestions.find((q) => q.id === questionId);
      if (!question) {
        persistAndRender(goToScreen(state, "result"));
        return;
      }
      track(
        { name: "differentiation_started", sessionId: state.sessionId, questionId: question.id },
        ctx(),
      );
      renderQuestion(app!, {
        eyebrow: "To tell them apart",
        prompt: question.prompt,
        progressLabel: "Differentiating question",
        options: question.options,
        selectedOptionId: state.answers.find((a) => a.questionId === question.id)?.optionId,
        onSelect: (optionId) => {
          state = answerQuestion(state, { questionId: question.id, optionId });
          track({ name: "differentiation_completed", sessionId: state.sessionId }, ctx());
          // El siguiente, no el mismo otra vez: `differentiatorFor` salta los
          // contestados. Antes se reprogramaba `question.id`, que con un solo
          // desempate en el banco no se notaba.
          const siguiente = needsDifferentiation(
            state.answers, bank, patternIds, state.differentiationAsked,
          )
            ? differentiatorFor(state.answers, bank, patternIds)
            : null;
          if (siguiente) {
            persistAndRender(scheduleDifferentiation(state, siguiente.id));
            return;
          }
          const result = currentResult();
          track(
            { name: "result_generated", sessionId: state.sessionId, resultState: result.state },
            ctx(),
          );
          persistAndRender(goToScreen(state, "result"));
        },
      });
      return;
    }
    case "result": {
      const result = currentResult();
      track({ name: "result_viewed", sessionId: state.sessionId, resultState: result.state }, ctx());
      const primaryPattern = result.primaryHypothesis
        ? (patterns[result.primaryHypothesis as keyof typeof patterns] ?? null)
        : null;
      const alternativePattern = result.alternativeHypothesis
        ? (patterns[result.alternativeHypothesis as keyof typeof patterns] ?? null)
        : null;
      const experiment = result.primaryHypothesis
        ? (EXPERIMENTS.find((e) => e.patternId === result.primaryHypothesis) ?? null)
        : null;
      renderResult(app!, {
        result,
        primaryPattern,
        alternativePattern,
        experiment,
        onContinue: () => persistAndRender(goToScreen(state, "reaction")),
      });
      renderTracePanel(app!, result);
      return;
    }
    case "reaction": {
      renderReaction(app!, (reaction) => {
        track({ name: "result_feedback", sessionId: state.sessionId, reaction }, ctx());
        persistAndRender(goToScreen(setReaction(state, reaction), "recommendation"));
      });
      return;
    }
    case "recommendation": {
      const result = currentResult();
      const recommendation = recommend(result, state.declaredBlocker, state.reaction ?? "fits");
      track(
        { name: "recommendation_viewed", sessionId: state.sessionId, route: recommendation.route },
        ctx(),
      );
      // A DÓNDE LLEVAN LOS BOTONES.
      //
      // Hasta el 18 de agosto los tres hacían lo mismo: `goToScreen("save")`.
      // O sea que la pantalla era decorativa — alguien acababa de dedicar
      // quince minutos, leía «esa parte es lo que trabaja Inner Architecture»,
      // pulsaba, y aterrizaba en una pantalla de guardar con la captura de
      // email apagada. El embudo entero moría en el punto de máxima intención.
      //
      // `keep_investigating` NO lleva a ningún producto, y es deliberado: se
      // emite cuando el resultado es `open` o la persona dice que no encaja.
      // Vender sobre un resultado débil quema la confianza que este test tarda
      // quince minutos en construir, y es justo lo que la marca dice no hacer.
      const SITIO = "https://inner-growth.co";
      const DESTINO: Record<string, string | null> = {
        inner_architecture: `${SITIO}/inner-architecture/`,
        inner_voice: `${SITIO}/inner-voice/`,
        keep_investigating: null,
      };
      const irA = (url: string | null) => {
        if (!url) { persistAndRender(goToScreen(state, "save")); return; }
        // Pestaña nueva: el resultado sigue detrás. Cerrar el mapa para ver una
        // página de producto sería cobrar el paso con lo único que la persona
        // se lleva.
        window.open(url, "_blank", "noopener");
      };
      renderRecommendation(
        app!,
        recommendation,
        () => {
          track(
            { name: "recommendation_clicked", sessionId: state.sessionId, route: recommendation.route },
            ctx(),
          );
          irA(DESTINO[recommendation.route] ?? null);
        },
        () => {
          track(
            { name: "recommendation_clicked", sessionId: state.sessionId, route: "inner_reset_secondary" },
            ctx(),
          );
          // El secundario es el 7-Day Reset, que NO es la suscripción: es una
          // muestra gratuita del método del libro, entregada por email.
          //
          // Apuntaba a /inner-voice/, la página de $9.99/mes. Dos cosas mal: era
          // otro producto, y gastaba la petición MÁS BARATA de todo el embudo
          // —un email a cambio de algo gratis— mandando a la gente a un muro de
          // pago. Justo aquí es donde más falta hace lo barato: quien acaba de
          // reconocerse pero no va a comprar hoy, si no deja el correo, se va
          // para siempre. La captura de Inner Map está apagada, así que ésta es
          // la única forma que hay de que esa persona vuelva.
          irA(`${SITIO}/#reset`);
        },
        () => persistAndRender(goToScreen(state, "save")),
      );
      return;
    }
    case "save": {
      renderSave(app!, () => {
        clearSession();
        state = createSession(situationOrder);
        persistAndRender(state);
      });
      return;
    }
  }
}

window.addEventListener("beforeunload", () => {
  if (state.screen !== "intro" && state.screen !== "save") {
    track({ name: "session_abandoned", sessionId: state.sessionId, screen: state.screen }, ctx());
  }
});

render();
